import { useEffect, useState, type ReactElement } from 'react';
import { toast } from '../../../design-system/components';
import { track, trackFailed } from '../../../../../shared-web/track';
import { absoluteShareUrl, copyText, share } from '../../../../../shared-web/share';
import { emberApi } from '../api';
import { BottomSheet } from './BottomSheet';
import './HandoffButton.css';

/**
 * Hand-off (v4.0 §4.18, 2026-09-06 拍板): the owner sends their own Ember to
 * someone they choose. One glyph in the toolbar row — the share glyph every
 * phone already teaches — and one sheet with two rows: the OS share sheet
 * (whichever apps the phone has: Messages, WhatsApp, Instagram, Snapchat,
 * Telegram…) and Copy link. Nothing per-network is built here; the OS sheet
 * is the person's own habit.
 *
 * The link is minted when the sheet opens, not when a row is tapped: the OS
 * share sheet must be called inside the tap's own activation, and a network
 * round-trip between the tap and `navigator.share` loses it on Safari. So the
 * rows wait (disabled, "One moment…") only for the brief mint, and every tap
 * on them is synchronous.
 *
 * The recipient reads the whole Ember: the link is the owner's consent. It is
 * not a v3.0 share — nothing enters a Request, the pool, or matching.
 *
 * The same control sends a weekly letter (§4.20, 2026-09-24 拍板): `subject`
 * names which — the API it mints from, the event names it records under, the
 * sheet's words. A letter's link is never taken back, so its sheet does not
 * promise that.
 */
export type HandoffSubject = { kind: 'ember'; id: string } | { kind: 'letter'; id: string };

const SUBJECTS = {
  ember: {
    mint: (id: string) => emberApi.mintHandoff(id),
    logAttr: 'ember_id',
    open: 'ember_handoff_open', send: 'ember_handoff_send', copy: 'ember_handoff_copy',
    title: 'Send this Ember to someone',
    description: 'Anyone with the link can read it in full. You can take the link back any time.',
  },
  letter: {
    mint: (id: string) => emberApi.mintLetterHandoff(id),
    logAttr: 'letter_id',
    open: 'letter_handoff_open', send: 'letter_handoff_send', copy: 'letter_handoff_copy',
    title: 'Send this letter to someone',
    description: 'Anyone with the link can read it in full.',
  },
} as const;

export function HandoffButton({ subject, title, handoffPath, onHandoffPath, className = '' }: {
  subject: HandoffSubject;
  title: string;
  /** The link this object already has, so the sheet opens on it without minting. */
  handoffPath: string | null | undefined;
  /** The link the sheet minted (the page keeps it for the next open). */
  onHandoffPath: (path: string) => void;
  className?: string;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const spec = SUBJECTS[subject.kind];
  return (
    <>
      <button
        type="button"
        className={`ember-handoff-button ${className}`.trim()}
        aria-label="Send to someone"
        data-log={spec.open}
        {...{ [`data-log-${spec.logAttr.replace('_', '-')}`]: subject.id }}
        onClick={() => setOpen(true)}
      >
        <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M12 16V3" />
          <path d="m7.5 7.5 4.5-4.5 4.5 4.5" />
          <path d="M5 11v7.5A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V11" />
        </svg>
        <span>Send</span>
      </button>
      {open && (
        <HandoffSheet
          subject={subject}
          title={title}
          handoffPath={handoffPath}
          onHandoffPath={onHandoffPath}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function HandoffSheet({ subject, title, handoffPath, onHandoffPath, onClose }: {
  subject: HandoffSubject;
  title: string;
  handoffPath: string | null | undefined;
  onHandoffPath: (path: string) => void;
  onClose: () => void;
}): ReactElement {
  const [path, setPath] = useState<string | null>(handoffPath || null);
  const [failed, setFailed] = useState(false);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const spec = SUBJECTS[subject.kind];
  const idProp = { [spec.logAttr]: subject.id };

  useEffect(() => {
    if (path) return undefined;
    let alive = true;
    spec.mint(subject.id)
      .then((r) => {
        if (!alive) return;
        setPath(r.handoff_path);
        onHandoffPath(r.handoff_path);
      })
      .catch(() => {
        if (!alive) return;
        trackFailed(spec.open, 'mint_failed', idProp);
        setFailed(true);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject.kind, subject.id]);

  const send = (): void => {
    if (!path) return;
    // Synchronous into navigator.share — see the component note.
    void share({ title, url: path }).then((outcome) => {
      track('ui.clicked', { name: spec.send, state: outcome, ...idProp });
      if (outcome === 'copied') toast.success('Link copied');
      if (outcome !== 'cancelled') onClose();
    }).catch(() => {
      trackFailed(spec.send, 'error', idProp);
      toast.error("Couldn't open sharing. Try Copy link.");
    });
  };

  const copy = (): void => {
    if (!path) return;
    void copyText(absoluteShareUrl(path)).then(() => {
      track('ui.clicked', { name: spec.copy, state: 'copied', ...idProp });
      toast.success('Link copied');
      onClose();
    }).catch(() => {
      track('ui.clicked', { name: spec.copy, state: 'failed', ...idProp });
      trackFailed(spec.copy, 'clipboard', idProp);
      toast.error("Couldn't copy. Long-press the link to copy it.");
    });
  };

  const waiting = !path && !failed;
  return (
    <BottomSheet
      onClose={onClose}
      title={spec.title}
      description={failed
        ? 'Something went wrong making the link. Close this and try again.'
        : spec.description}
      logSeen="handoff_sheet_open"
      logSeenAttrs={{ [`data-log-${spec.logAttr.replace('_', '-')}`]: subject.id }}
      className="handoff-sheet"
    >
      {canShare && (
        <button
          type="button"
          className="ember-btn ember-btn-primary handoff-sheet-btn"
          disabled={waiting || failed}
          onClick={send}
        >
          {waiting ? 'One moment…' : 'Send to someone'}
        </button>
      )}
      <button
        type="button"
        className={`ember-btn ${canShare ? 'ember-btn-secondary' : 'ember-btn-primary'} handoff-sheet-btn`}
        disabled={waiting || failed}
        onClick={copy}
      >
        {waiting && !canShare ? 'One moment…' : 'Copy link'}
      </button>
    </BottomSheet>
  );
}
